/**
 * №48 — a record's sub-views are TABS ON THE RECORD, and the rail does not know
 * a record is open.
 *
 * **David ruled it 2026-08-17**, walking back the whole E-7 exploration:
 * *"we will revert to regular tabs on the entity page. Keep navigation
 * unaffected by the current record and do not list the current record AT ALL…
 * This is a walkback of the entire idea."*
 *
 * That reverses the PLACEMENT half of №29 §3 ④ — S4 deleted the matter's in-page
 * tabs to build the rail's OPEN sub-nav, and this puts them back. **The
 * declaration seam is untouched**, which is the whole reason this is a small
 * change: `openRecordNav()` already returns the sub-views an entity type
 * declares, in order, each as a concrete path, and `subViewCounts` already
 * carries the counts the record itself declared. Both were built for the rail;
 * neither cares who renders them. Nothing is re-typed here, so a declared view
 * cannot appear in one surface and not the other.
 *
 * Mounted ONCE, in the shell, immediately under the subject header — not per
 * page. Each sub-view is its own route (`/app/place/:entity/:id/parties`, …),
 * so a per-page mount would be the same strip pasted into every record
 * component, and the day one of them forgot it the record would lose its
 * navigation on exactly one tab. `openRecordNav` returns null off the record
 * family, and then this renders nothing at all.
 *
 * Vocabulary is the shipped one — `.tabs` / `.tab` / `.tab--on` / `.cnt` from
 * `app-chrome.css`, "the ONE tab system" (R1). No new stylesheet, no new class
 * for the strip itself — the drawn `.rectabs` anatomy
 * (`docs/53-entity-layout-options/kuvio.css:31-36`) is this SAME underline
 * strip, so its metrics are tuned into the existing `.tab`/`.tabs` rules
 * (53.3.1 S0) rather than duplicated under a second name.
 *
 * ## №53.3.1 S0 — ordering + overflow, added this rung
 *
 * `openRecordNav` still returns items in DECLARATION order (`record-subviews.ts`,
 * which is a table of what exists, not a reading order). D-CR26 rules the strip's
 * actual left-to-right order — `record-tab-order.ts`'s `orderRecordTabs`, applied
 * here before render, using the same `counts` channel the badges already read.
 *
 * The strip also now FOLDS instead of only scrolling: `Stream · Process · Billing
 * · Trust` stay pinned, and anything after them collapses behind a `More ▾`
 * trigger the moment the strip's natural width exceeds its container's — the
 * 408px-panel-open state (VISION-2) is the case this exists for. A hidden
 * measurement row (`.tabs--measure`, `visibility:hidden` and off-screen via
 * `position:fixed`) renders every item's natural width before the visible pass
 * decides what fits — the same "measure, then decide" shape №48's own 818px
 * comment used, done as arithmetic here instead of a fitted constant because the
 * panel's 408px push makes the available width genuinely dynamic.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { NavLink, useLocation } from "react-router";
import { openRecordNav } from "./route-anchor-matrix";
import { useSubViewCountsValue } from "./stage-head";
import { orderRecordTabs } from "./record-tab-order";

/** D-CR26's "always visible" set — never folded, whatever the width. */
const PINNED_KEYS = new Set(["place-record", "place-process", "place-record-billing", "place-record-trust"]);

/** Tracks `.tabs { gap }` in app-chrome.css. A measured constant rather than a
 *  read of computed style — the strip is short and this rung's own restyle
 *  fixes the value, the same trade №48's 818px comment already made. */
const GAP_PX = 2;

export function RecordTabs() {
  const location = useLocation();
  const counts = useSubViewCountsValue();
  const open = useMemo(() => openRecordNav(location.pathname), [location.pathname]);
  const ordered = useMemo(() => (open ? orderRecordTabs(open.items, counts) : []), [open, counts]);

  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState<number>(ordered.length);

  const moreBtnRef = useRef<HTMLButtonElement>(null);
  const moreMenuRef = useRef<HTMLDivElement>(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [moreCoords, setMoreCoords] = useState<{ top: number; left: number } | null>(null);

  const pinnedCount = useMemo(() => {
    let n = 0;
    while (n < ordered.length && PINNED_KEYS.has(ordered[n]!.key)) n++;
    return n;
  }, [ordered]);

  const recalc = useCallback(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure || ordered.length === 0) {
      setVisibleCount(ordered.length);
      return;
    }
    // The measurement row is every ordered item plus one "More ▾"-shaped proxy,
    // always last — see the JSX below.
    const kids = Array.from(measure.children) as HTMLElement[];
    if (kids.length !== ordered.length + 1) {
      setVisibleCount(ordered.length);
      return;
    }
    const widths = kids.map((el) => el.getBoundingClientRect().width);
    const moreWidth = widths[widths.length - 1]!;
    const itemWidths = widths.slice(0, -1);

    const available = container.clientWidth;
    const fullWidth = itemWidths.reduce((a, b) => a + b, 0) + GAP_PX * Math.max(0, itemWidths.length - 1);
    if (fullWidth <= available) {
      setVisibleCount(ordered.length);
      return;
    }

    // The pinned prefix is NEVER folded, even if it alone does not fit — the
    // ruling is "always visible", not "visible when there is room".
    let sum = itemWidths.slice(0, pinnedCount).reduce((a, b) => a + b, 0) + GAP_PX * Math.max(0, pinnedCount - 1);
    let count = pinnedCount;
    const budget = available - moreWidth - GAP_PX; // room left once the trigger itself is shown
    for (let i = pinnedCount; i < itemWidths.length; i++) {
      const next = sum + (count > 0 ? GAP_PX : 0) + itemWidths[i]!;
      if (next > budget) break;
      sum = next;
      count = i + 1;
    }
    setVisibleCount(Math.max(pinnedCount, count));
  }, [ordered, pinnedCount]);

  // Layout effect (not a passive one) — runs before paint, so a narrow mount
  // never flashes the unfolded strip first.
  useLayoutEffect(() => {
    recalc();
  }, [recalc]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => recalc());
    ro.observe(container);
    return () => ro.disconnect();
  }, [recalc]);

  useLayoutEffect(() => {
    if (!moreOpen) return;
    const btn = moreBtnRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    setMoreCoords({ top: r.bottom + 4, left: r.left });
  }, [moreOpen]);

  useEffect(() => {
    if (!moreOpen) return;
    function onDocClick(e: MouseEvent) {
      const t = e.target as Node;
      if (moreBtnRef.current?.contains(t)) return;
      if (moreMenuRef.current?.contains(t)) return;
      setMoreOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [moreOpen]);

  // A navigation — including picking a folded tab — closes the menu. The
  // React-blessed "store information from previous renders" pattern (setState
  // DURING RENDER, no effect, no cascade) — the same one AppShellLayout.tsx
  // uses to close the nav drawer on navigation, and for the same reason:
  // `useEffect(() => setMoreOpen(false), [location.pathname])` is an
  // unconditional setState in an effect body, which `react-hooks/set-state-in-effect`
  // correctly flags (cascading renders) — this repo already hit that class of
  // defect once in the S1 hooks work and it stayed invisible to `tsc`/`vite`.
  // https://react.dev/reference/react/useState#storing-information-from-previous-renders
  const [lastPath, setLastPath] = useState(location.pathname);
  if (lastPath !== location.pathname) {
    setLastPath(location.pathname);
    if (moreOpen) setMoreOpen(false);
  }

  // Not a record, or a record whose type declares nothing: render NOTHING. A tab
  // strip with one tab is chrome pretending to be navigation — the same rule that
  // kept the rail's OPEN heading from rendering over an empty section.
  if (!open || open.items.length < 2) return null;

  const visible = ordered.slice(0, visibleCount);
  const folded = ordered.slice(visibleCount);
  const activePath = location.pathname.replace(/\/+$/, "");
  const activeIsFolded = folded.some((it) => it.path.replace(/\/+$/, "") === activePath);

  return (
    <>
      <div className="tabs" data-testid="record-tabs" ref={containerRef}>
        {visible.map((it) => (
          <NavLink
            key={it.key}
            to={it.path}
            end
            className={({ isActive }: { isActive: boolean }) => `tab${isActive ? " tab--on" : ""}`}
            data-testid={`record-tab-${it.key}`}
          >
            {it.label}
            {/* The count the RECORD declared for this row (§3 ④). Undefined renders
                nothing, which is not the same as 0: no badge = not loaded yet,
                "0" = the record genuinely has none. Carried over from the rail
                unchanged — it was always the record's number, not the nav's. */}
            {counts[it.key] != null && <span className="cnt">{counts[it.key]}</span>}
          </NavLink>
        ))}
        {folded.length > 0 && (
          <button
            type="button"
            ref={moreBtnRef}
            className={`tab tab--more${activeIsFolded ? " tab--on" : ""}`}
            onClick={() => setMoreOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={moreOpen}
            data-testid="record-tabs-more"
          >
            More <span className="tab--more__chev" aria-hidden="true">▾</span>
          </button>
        )}
      </div>

      {/* Off-screen measurement row — every ordered item's REAL rendered width,
          under the exact same classes as the visible pass, plus one "More ▾"
          proxy always last. `position:fixed` + far off-screen removes it from
          any layout flow (no positioned-ancestor coupling needed) while still
          giving each child a true `getBoundingClientRect()`. */}
      <div className="tabs tabs--measure" ref={measureRef} aria-hidden="true">
        {ordered.map((it) => (
          <span key={it.key} className="tab">
            {it.label}
            {counts[it.key] != null && <span className="cnt">{counts[it.key]}</span>}
          </span>
        ))}
        <span className="tab tab--more">More <span className="tab--more__chev">▾</span></span>
      </div>

      {moreOpen && folded.length > 0 &&
        createPortal(
          <div
            ref={moreMenuRef}
            role="menu"
            className="tabsmore"
            style={{ top: moreCoords?.top ?? 0, left: moreCoords?.left ?? 0, visibility: moreCoords ? "visible" : "hidden" }}
            data-testid="record-tabs-more-menu"
          >
            {folded.map((it) => (
              <NavLink
                key={it.key}
                to={it.path}
                end
                role="menuitem"
                className={({ isActive }: { isActive: boolean }) => `tabsmore__item${isActive ? " tabsmore__item--on" : ""}`}
              >
                {it.label}
                {counts[it.key] != null && <span className="cnt">{counts[it.key]}</span>}
              </NavLink>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}
